import type { Client } from '../../api/client.js';
import { ROUTES } from '../../api/contrat.js';
import type { Decision, DonneesPilotage, Echeance, RelanceAValider, TableauPilotage } from './modele.js';

export interface SourcePilotage {
  charger: () => Promise<DonneesPilotage>;
  trancher: (decisionId: string, option: string, commentaire?: string) => Promise<Decision>;
  validerRelance: (messageId: string, decision: 'valider' | 'rejeter', raison?: string) => Promise<void>;
}

/**
 * Route du contrat (RFC-L51-001 §2) : les messages sortants en attente de validation. Si elle manquait au contrat ou échouait,
 * la liste des relances à valider serait déclarée indisponible (échec sûr : rien n'est deviné, la raison est affichée).
 */
export const ROUTE_RELANCES = 'messagerie.sortants.lister';
export const MESSAGE_RELANCES_INDISPONIBLES =
  'La liste des relances à valider n’est pas disponible pour le moment. Réessayez dans un instant.';

const routeDuContrat = (routeId: string): boolean => ROUTES.some((r) => r.id === routeId);

export function creerSource(client: Client, options: { disponible?: (routeId: string) => boolean } = {}): SourcePilotage {
  const disponible = options.disponible ?? routeDuContrat;

  async function relances(): Promise<{ liste: RelanceAValider[]; indisponible: string | null }> {
    if (!disponible(ROUTE_RELANCES)) return { liste: [], indisponible: MESSAGE_RELANCES_INDISPONIBLES };
    try {
      const lues = await client.appeler<RelanceAValider[]>(ROUTE_RELANCES, { requete: { etat: 'pending_validation' } });
      return { liste: lues.filter((m) => m.state === 'pending_validation'), indisponible: null };
    } catch {
      return { liste: [], indisponible: MESSAGE_RELANCES_INDISPONIBLES };
    }
  }

  return {
    async charger() {
      const [tableau, decisions, echeances, aValider] = await Promise.all([
        client.appeler<TableauPilotage>('dossiers.pilotage'),
        client.appeler<Decision[]>('decisions.lister'),
        client.appeler<Echeance[]>('echeances.lister'),
        relances(),
      ]);
      return {
        tableau,
        decisions: decisions.filter((d) => d.decidedOption === null),
        echeances,
        relances: aValider.liste,
        relancesIndisponible: aValider.indisponible,
      };
    },
    trancher(decisionId, option, commentaire) {
      return client.appeler<Decision>('decisions.trancher', {
        parametres: { decisionId },
        corps: commentaire === undefined ? { option } : { option, comment: commentaire },
      });
    },
    async validerRelance(messageId, decision, raison) {
      await client.appeler('messagerie.valider', {
        parametres: { messageId },
        corps: raison === undefined ? { decision } : { decision, reason: raison },
      });
    },
  };
}
